import { createSlice } from '@reduxjs/toolkit';
import { GlobalState } from '../../../types.global';
import { selectOnboardingStep } from '../../user/slice';
import { selectFeatureFlags } from '../../../store/selectors';
import { DialogTypes, TriviaState } from './types';
import { getRandomIntInRange } from './utils';
import { browserStorageUpdate } from '../../../store/actions';

const today = new Date().setHours(0, 0, 0, 0);
const triviaAmount = 189;

const initialState = {
  dialogMode: null,
  trivia: null,
  visibility: {
    lastSeen: null,
    tabs: null,
    maxTabs: 5,
    totalCount: 0,
  },
  answers: [],
  disabled: false,
  showTrivia: false,
};

export const getInitialTriviaState = (state: GlobalState): TriviaState => {
  if (!state.trivia) return initialState;

  const { trivia, user } = state;
  const onboardingStep = selectOnboardingStep(state);
  const featureFlags = selectFeatureFlags(state);

  const showTrivia =
    // !featureFlags?.SHELL_TRIVIA_OFF &&
    !trivia.disabled &&
    user.shells.shellsCounter >= 100 &&
    trivia.answers.length < triviaAmount &&
    !onboardingStep &&
    trivia.visibility.totalCount === 2;

  return {
    ...trivia,
    dialogMode: null,
    visibility: {
      ...trivia.visibility,
      maxTabs:
        trivia.visibility.lastSeen === today ? trivia.visibility.maxTabs : 5,
      totalCount:
        trivia.visibility.totalCount >= 2
          ? 0
          : trivia.visibility.totalCount + 1,
    },
    showTrivia,
  };
};

export const triviaSlice = createSlice({
  name: 'trivia',
  initialState,
  reducers: {
    answeredQuestion: (state: TriviaState, { payload }): void => {
      if (!state.trivia) return;

      const isCorrect = payload === state.trivia.answers.correctId;

      if (isCorrect) {
        state.answers = [...state.answers, state.trivia.id];
      }

      state.dialogMode = isCorrect
        ? DialogTypes.CorrectAnswer
        : DialogTypes.IncorrectAnswer;

      state.visibility = {
        ...state.visibility,
        maxTabs: state.visibility.maxTabs + 10,
      };
    },
    updatedVisibility: (state: TriviaState, { payload }): void => {
      state.visibility = {
        ...state.visibility,
        ...payload,
      };
    },
    setDialogMode: (state: TriviaState, { payload }): void => {
      state.dialogMode = payload;
    },
    setCurrentTrivia: (state: TriviaState, { payload }): void => {
      state.trivia = payload;
    },
    clearedTrivia: (state: TriviaState): void => {
      state.trivia = null;
      state.dialogMode = null;
    },
    disabledTrivia: (state: TriviaState): void => {
      state.disabled = true;
      state.showTrivia = false;
      state.trivia = null;
      state.dialogMode = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(browserStorageUpdate, (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialTriviaState(payload.state);
      }
    });
  },
});

export const {
  answeredQuestion,
  setDialogMode,
  updatedVisibility,
  setCurrentTrivia,
  clearedTrivia,
  disabledTrivia,
} = triviaSlice.actions;

export default triviaSlice.reducer;
